import { motion } from "framer-motion";
import { ArrowRight, ChevronRight } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { WHATSAPP_URL } from "@/lib/whatsapp";
import heroBg from "@/assets/hero-industrial.jpg";

const HeroSection = () => {
  const { lang, t } = useI18n();
  const stats = t.header.stats[lang];

  return (
    <header className="relative bg-brand-black overflow-hidden">
      {/* Industrial background photo */}
      <div className="absolute inset-0">
        <img
          src={heroBg}
          alt=""
          aria-hidden="true"
          width={1920}
          height={1080}
          loading="eager"
          decoding="async"
          fetchPriority="high"
          className="w-full h-full object-cover opacity-55"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-brand-black/85 via-brand-black/65 to-brand-black" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_40%,hsl(var(--brand-blue)/0.18),transparent_60%)]" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-black via-transparent to-brand-black/40" />
      </div>

      <div className="container mx-auto px-6 max-w-[960px] relative z-10 pt-32 pb-12 md:pt-40 md:pb-16">



        {/* Monumental headline */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="mt-12 md:mt-16 font-display text-white leading-[0.9] tracking-[0.01em]"
          style={{ fontSize: "clamp(3.25rem, 11vw, 8.5rem)" }}
        >
          <span className="block">{t.header.headlineLine1[lang]}</span>
          <span className="block text-brand-blue">{t.header.headlineLine2[lang]}</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.35 }}
          className="mt-8 max-w-[600px] text-[15px] md:text-base font-light text-white/80 leading-[1.7]"
        >
          {t.header.sublead[lang]}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.5 }}
          className="mt-10 flex flex-col sm:flex-row gap-3"
        >
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-sm text-xs font-semibold uppercase tracking-[0.2em] hover:opacity-90 transition-opacity"
          >
            {t.header.ctaPrimary[lang]}
            <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
          </a>
          <a
            href="#what"
            className="group inline-flex items-center gap-2 border border-white/20 text-white px-6 py-3 rounded-sm text-xs font-semibold uppercase tracking-[0.2em] hover:border-brand-blue hover:text-brand-blue transition-colors"
          >
            {t.header.ctaSecondary[lang]}
            <ChevronRight size={14} className="group-hover:translate-x-1 transition-transform" />
          </a>
        </motion.div>
      </div>

      {/* Stat bar */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.7, delay: 0.7 }}
        className="relative z-10 border-t border-white/10 bg-brand-black/70 backdrop-blur-sm"
      >
        <div className="container mx-auto px-6 max-w-[960px]">
          <dl className="grid grid-cols-3 divide-x divide-white/10 -mx-2">
            {stats.map((s) => (
              <div key={s.label} className="px-4 py-4 md:py-5">
                <dt className="font-display text-2xl md:text-3xl text-brand-blue leading-none">
                  {s.value}
                </dt>
                <dd className="mt-1.5 text-[10px] md:text-[10.5px] font-semibold uppercase tracking-[0.18em] text-white/70 leading-tight">
                  {s.label}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </motion.div>
    </header>
  );
};

export default HeroSection;
